import { Controller } from '@hotwired/stimulus'
import computedValues from '../../helpers/computedValues'
import { track } from '../../helpers/controllerHelpers/savvyAnalytics'
import {
  connectUserData,
  doNotStoreComponentDataInCloud,
  getComponentContext,
  getFlow,
  lifecycleTrigger,
} from '../../helpers/controllerHelpers/utils'
import { debounce } from '../../helpers/debounce'
import { BookMeeting, UserData } from '../../api/models/types'
import jitsu from '../../helpers/jitsu'

type UserDataValue = {
  name?: string
  last_name?: string
  first_name?: string
  email?: string
  customAnswers?: number
}

declare global {
  interface Window {
    Calendly: any
  }
}

export default class X extends Controller {
  static targets = ['loading', 'container']
  static values = {
    service: String,
  }

  declare readonly serviceValue: string
  declare readonly loadingTarget: HTMLElement
  declare readonly containerTarget: HTMLElement
  disconnectUserData: (() => void) | undefined
  userDataValueChanged: (userDataValue?: UserDataValue) => void
  flowId: string | null
  component: BookMeeting
  pageId: string
  cleanup: () => void
  declare hasContainerTarget: boolean
  declare hasLoadingTarget: boolean
  debounceListenHubspot: (...args: any[]) => void
  componentId: string
  declare userDataValue: UserDataValue
  listenHubspot: () => void
  flowEl: Element | null

  connect() {
    const { component, flowId, pageId, flowEl } = getComponentContext(this)
    this.disconnectUserData = connectUserData(this, this.userDataValueChanged)
    this.flowId = flowId
    this.component = component as BookMeeting
    this.pageId = pageId
    this.flowEl = flowEl

    this.loadScriptTag()

    lifecycleTrigger(this, 'mounted')
  }

  disconnect() {
    lifecycleTrigger(this, 'unmounted')
    if (typeof this.disconnectUserData === 'function') this.disconnectUserData()
    if (typeof this.cleanup === 'function') this.cleanup()
  }

  initCalendly(embedUrl: string, script: HTMLScriptElement) {
    if (!this.hasContainerTarget) return
    if (this.hasLoadingTarget) this.loadingTarget.remove()

    const prefill: {
      name?: string
      lastName?: string
      firstName?: string
      email?: string
      customAnswers?: Record<string, unknown>
    } = {
      name: this.userDataValue.name,
      lastName: this.userDataValue.last_name,
      firstName: this.userDataValue.first_name,
      email: this.userDataValue.email,
    }

    if (Array.isArray(this.component.prefill_keys)) {
      prefill.customAnswers = this.component.prefill_keys.reduce((acc, k, i) => {
        if (k === '__empty') return acc
        const key = `a${i + 1}`
        const val = this.userDataValue[k]
        if (val !== undefined && val !== null) acc[key] = val
        return acc
      }, {})
    }
    window.Calendly.initInlineWidget({
      url: embedUrl,
      parentElement: this.containerTarget,
      prefill,
      utm: {},
    })

    window.addEventListener('message', this.listenForUpdate)
    this.cleanup = () => {
      script.remove()
      window.removeEventListener('message', this.listenForUpdate)
    }
  }

  initHubspot() {
    if (this.hasLoadingTarget) this.loadingTarget.remove()
    window.addEventListener('message', this.debounceListenHubspot)
  }

  loadScriptTag() {
    if (typeof this.cleanup === 'function') this.cleanup()
    const script = document.createElement('script')
    const embedUrl = computedValues(this.component.embedUrl, this.userDataValue)

    switch (this.serviceValue) {
      case 'calendly': {
        script.setAttribute('src', `https://assets.calendly.com/assets/external/widget.js`)
        script.addEventListener('load', this.initCalendly.bind(this, embedUrl, script))

        /**
         * During testing, we never let the widget load, so we need to manually
         * call the init function
         */
        if (this.component.isTesting) {
          this.initCalendly(embedUrl, script)
        }

        break
      }

      case 'hubspot': {
        if (!embedUrl || !(embedUrl.startsWith('http://') || embedUrl.startsWith('https://')))
          return
        if (!this.hasContainerTarget) return
        script.setAttribute(
          'src',
          `https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js`
        )

        const base = embedUrl
        const prefill = this.component.prefill || {}
        const params = Object.entries(prefill).reduce((acc: string[], e) => {
          if (e[1]) {
            const str = `${e[0]}=${computedValues(e[1], this.userDataValue)}`
            acc.push(str)
          }
          return acc
        }, [])

        const embedUrlFull = params.length
          ? `${base}${base.includes('?') ? '&' : '?'}${params.join('&')}`
          : base

        this.containerTarget.setAttribute('data-src', embedUrlFull)

        this.debounceListenHubspot = debounce(this.listenForHubspotBooking, 250)
        script.addEventListener('load', () => {
          this.initHubspot()
        })

        /**
         * During testing, we never let the widget load, so we need to manually
         * call the init function
         */
        if (this.component.isTesting) {
          this.initHubspot()
        }

        this.cleanup = () => {
          script.remove()
          window.removeEventListener('message', this.debounceListenHubspot)
        }
        break
      }

      default:
        break
    }

    const existingTags = document.querySelectorAll(`script[src='${script.src}']`)
    existingTags.forEach((el) => el.remove())
    document.head.appendChild(script)
  }

  listenForUpdate = (e: { data: { event: string | string[]; payload: any } }) => {
    const isCalendlyEvent = e.data.event?.indexOf('calendly') === 0
    if (isCalendlyEvent) {
      const status = getStatus(String(e.data.event))
      if (!status) return

      const trackingEventName = getAnalyticsEventName(status)
      this.updateBookStatus(status)

      //* If store_scheduled_event_id is set, store the scheduled event id in that key in userData
      if (this.component.store_scheduled_event_id) {
        const scheduledEventId = e.data.payload?.event?.uri?.split('/').pop()
        this.dispatch('update', {
          detail: { key: this.component.store_scheduled_event_id, value: scheduledEventId },
        })
      }

      if (trackingEventName && !doNotStoreComponentDataInCloud(this.component)) {
        jitsu.track(trackingEventName, { flow_id: this.flowId || '', meeting_service: 'calendly' })
        track(this.flowId || '', `calendly.${status}`, e.data.payload)
      }
    }
  }

  listenForHubspotBooking = (e: {
    origin: string
    data: { height: string; meetingBookSucceeded: boolean }
  }) => {
    if (e.origin !== 'https://meetings.hubspot.com') return

    const flow = getFlow(this, true)
    const pageIndex = flow?.pages.findIndex((page) => page.id === this.pageId)
    const componentIndex = flow?.pages[pageIndex]?.components.findIndex(
      (component) => component.id === this.component.id
    )

    if (e && e.data && e.data.height) {
      track(this.flowId || '', `hubspot.meeting_viewed`)
      this.updateBookStatus('viewed')
      if (!doNotStoreComponentDataInCloud(this.component)) {
        jitsu.track('meeting:viewed', { flow_id: this.flowId || '', meeting_service: 'hubspot' })
      }

      window.Savvy.addEventLogs?.([
        {
          flow_id: this.flowId || '',
          event_type: 'meeting:viewed',
          timestamp: Date.now(),
          user_data_snapshot: this.userDataValue as UserData,
          data: {
            component_id: this.component.id,
            component_key: this.component.key,
            component_path: [
              ...(this.pageId ? ['pages', `${pageIndex}`] : []),
              'components',
              `${componentIndex}`,
            ],
          },
        },
      ])
    }
    if (e && e.data && e.data.meetingBookSucceeded) {
      track(this.flowId || '', `hubspot.meeting_booked`)
      this.updateBookStatus('booked')

      if (!doNotStoreComponentDataInCloud(this.component)) {
        jitsu.track('meeting:booked', { flow_id: this.flowId || '', meeting_service: 'hubspot' })
      }

      window.Savvy.addEventLogs?.([
        {
          flow_id: this.flowId || '',
          event_type: 'meeting:booked',
          timestamp: Date.now(),
          user_data_snapshot: this.userDataValue as UserData,
          data: {
            component_id: this.component.id,
            component_key: this.component.key,
            component_path: [
              ...(this.pageId ? ['pages', `${pageIndex}`] : []),
              'components',
              `${componentIndex}`,
            ],
          },
        },
      ])
    }
  }

  async updateBookStatus(value: string) {
    const key = this.component.key || this.componentId
    const tier = eventTier(value)
    const prevTier = eventTier(this.userDataValue[key])

    if ([0, 1, 2].includes(tier)) {
      const actions = this.component[`outputs_tier_${tier + 1}`]

      if (Array.isArray(actions))
        this.dispatch('actions', {
          detail: {
            actions: actions,
            component: this.component,
          },
        })
      if (this.component[`conversions_tier_${tier + 1}`]) {
        const conversionsToOutputs = this.component[`conversions_tier_${tier + 1}`].map((c) => ({
          ...c,
          output: 'conversion',
        }))
        this.dispatch('actions', {
          detail: {
            actions: conversionsToOutputs,
            component: this.component,
          },
        })
      }
    }

    if (tier >= prevTier) {
      this.dispatch('update', { detail: { key, value } })
    }

    //* When reaching booked if nextOnComplete is true, dispatch next
    if (tier === 2) {
      if (this.component.next_on_complete) {
        if (
          this.component.next_on_complete_timeout &&
          typeof this.component.next_on_complete_timeout === 'number'
        ) {
          setTimeout(() => {
            this.dispatch('next', { detail: { value: this.pageId } })
          }, this.component.next_on_complete_timeout)
        } else {
          this.dispatch('next', { detail: { value: this.pageId } })
        }
      }
    }
  }
}

function eventTier(event: string) {
  const eventTiers = [['loaded', 'viewed'], ['selected_timeslot'], ['scheduled', 'booked']]
  const tier = eventTiers.findIndex((t) => t.includes(event))
  return tier === -1 ? 0 : tier
}

function getStatus(eventName: string) {
  const key = eventName.split('.')[1] || eventName
  switch (eventName.split('.')[1]) {
    case 'profile_page_viewed':
    case 'event_type_viewed': {
      return 'loaded'
    }
    case 'date_and_time_selected': {
      return 'selected_timeslot'
    }
    case 'event_scheduled': {
      return 'scheduled'
    }
    case 'page_height': {
      return null
    }
    default:
      return key
  }
}

function getAnalyticsEventName(status: string) {
  switch (status) {
    case 'loaded':
      return 'meeting:viewed'
    case 'selected_timeslot':
      return 'meeting:chose_timeslot'
    case 'scheduled':
      return 'meeting:booked'
    default:
      return null
  }
}
